import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import FileUpload from "../../components/common/fileUpload";
import dashboardService from "../../services/DashboardServices";
import { showToast } from "../../components/common/toastNotification";
import { Wallpaper, WallpaperErrors } from "../../types/dashboard";

interface WallpaperModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  wallpaperUpdateData?: Wallpaper | null;
}

const WallpaperModal: React.FC<WallpaperModalProps> = ({
  open,
  onClose,
  onSave,
  wallpaperUpdateData,
}) => {
  const [formData, setFormData] = useState<Wallpaper>({
    id: null,
    title: "",
    url: "",
    file: null,
  });

  const [formErrors, setFormErrors] = useState<WallpaperErrors>({
    title: "",
    url: "",
  });

  useEffect(() => {
    if (wallpaperUpdateData) {
      setFormData({
        id: wallpaperUpdateData.id || null,
        title: wallpaperUpdateData.title || "",
        url: wallpaperUpdateData.url || "",
        file: wallpaperUpdateData.filePath || null,
      });
    } else {
      setFormData({
        id: null,
        title: "",
        url: "",
        file: null,
      });
    }
  }, [wallpaperUpdateData, open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };
  const validateForm = () => {
    const errors: WallpaperErrors = {};
    const urlRegex =
      /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }

    const url = formData.url?.trim() || "";

    if (!url && !formData.file) {
      errors.url = "Either an image file or an image URL must be provided.";
      showToast(errors.url, "error");
    } else if (url && !urlRegex.test(url)) {
      errors.url = "Enter a valid image URL.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;

    try {
      const { id, title, url, file } = formData;
      const formDataToSend = new FormData();
      formDataToSend.append("Id", id?.toString() || "0");
      formDataToSend.append("Title", title.trim());

      if (file instanceof File) {
        formDataToSend.append("file", file);
        formDataToSend.append("url", "");
        formDataToSend.append("IsLink", "false");
      } else if (url?.trim()) {
        formDataToSend.append("url", url.trim());
        formDataToSend.append("file", "");
        formDataToSend.append("IsLink", "true");
      } else if (id && wallpaperUpdateData?.filePath) {
        formDataToSend.append("url", wallpaperUpdateData.filePath);
        formDataToSend.append("file", "");
        formDataToSend.append("IsLink", "false");
      }

      const apiCall = id
        ? dashboardService.updateWallpaperDataAPI
        : dashboardService.addWallpaperAPI;

      await apiCall(formDataToSend)
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            onSave();
            onClose();
          } else {
            showToast(response?.data?.message || "Error occurred", "error");
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={() => {
        setFormData({ id: null, title: "", url: "", file: null });
        onClose();
      }}
      onSave={handleSaveData}
      title="Wallpapers"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <FileUpload
            fileTypeLabel="Image"
            isSingleSelected={false}
            fileUrl={
              formData.file instanceof File
                ? URL.createObjectURL(formData.file)
                : formData.file
            }
            onChange={(files) => {
              const file = files[0] || null;
              setFormData((prev) => ({ ...prev, file }));

              if (file) {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  url: "",
                }));
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <CustomInput
            label="Title"
            type="text"
            name="title"
            placeholder="Enter Title"
            value={formData.title}
            onChange={handleChange}
            error={formErrors.title}
          />
        </Grid>

        <Grid item xs={12}>
          <CustomInput
            label="Image URL"
            type="text"
            name="url"
            placeholder="Enter Image URL"
            value={formData.url || ""}
            onChange={handleChange}
            error={formErrors.url}
          />
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default WallpaperModal;
